import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { FullRightAccess, ViewRightType, hasGlobalRightAccess } from "@dto/Users";
import { ObjectInfos } from "@dto/objects";
import { View } from "@dto/views";
import { GRAY_800_COLOR } from "@utils/colors";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { updateStorageSelection } from "@utils/localStorage";
import { toastError } from "@utils/toasts";
import { TW_ViewListItem, TW_ViewListItemIcon, TW_ViewListItemPrivateIcon, TW_ViewListItemSelected, TW_ViewListItemSharedIcon, TW_ViewListItemText, TW_ViewListItemTooltipContent, TW_ViewListItemTooltipText, getWsButtonStyle } from "@utils/twClasses";
import { put } from "@utils/utils";
import { People, Profile, RowVertical } from "iconsax-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { translate } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import DropLineIndicator from "./DropLineIndicator";
import { FOLDER_DRAG_TYPE, VIEW_DRAG_TYPE } from "./ViewFolderItem";
import ViewMenuButton from "./ViewMenuButton";

interface DraggableViewListItemProps {
    object: ObjectInfos;
    view: View;
    // Personal folder holding this row (null = root); filing is personal, so
    // every user can drag a view toward a folder whatever their rights
    containerFolderId?: string | null;
    // Drop between two views of a folder: pure personal reorder handled by the
    // parent (no write on the shared view order)
    onDropInFolder?: (draggedViewKey: string, targetViewKey: string, containerFolderId: string, position: 'top' | 'bottom') => void;
    // A view dropped among the root views leaves its folder
    onMovedToRoot?: (viewKey: string) => void;
    // False while the dragged view cannot be reordered by this user: the root
    // rows then refuse the drop (their order is the shared view order)
    draggedViewReorderable?: boolean;
    onMobileClose?: () => void;
    onViewOrderUpdate?: (view: View) => void;
    isDragging?: boolean;
    onDragStart?: (viewKey: string) => void;
    onDragEnd?: () => void;
    onDragOver?: (viewKey: string, position: 'top' | 'bottom' | null) => void;
    showDropLineTop?: boolean;
    showDropLineBottom?: boolean;
}

function DraggableViewListItem({
    object,
    view,
    containerFolderId = null,
    onDropInFolder,
    onMovedToRoot,
    draggedViewReorderable = true,
    onMobileClose,
    onViewOrderUpdate,
    isDragging = false,
    onDragStart,
    onDragEnd,
    onDragOver,
    showDropLineTop = false,
    showDropLineBottom = false
}: DraggableViewListItemProps) {

    const workspace = useModelStore(state => state.selectedWorkspace);
    const selectView = useModelStore(state => state.selectView);
    const selectedView = useModelStore(state => state.selectedView);
    const navigate = useNavigate();
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const wsColor = useModelStore(state => state.wsColor);
    const wsIsDarkColor = useModelStore(state => state.wsIsDarkColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const [isHover, setIsHover] = useState(false);
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const dragRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();

    const onSelection = () => {
        if (!workspace?._key) {
            console.error("Workspace key is not set");
            return;
        }

        // Always close mobile sidebar when clicked
        if (onMobileClose) {
            onMobileClose();
        }

        if (selectedView?._key !== view._key) {
            selectView(object, view, null);
            updateStorageSelection(workspace?._key, object.object?._key, view?._key, null);
            navigate(`/workspaces/${workspace._key}/objects`);
        }
    }

    const handleDragStart = (e: React.DragEvent) => {
        e.dataTransfer.setData(VIEW_DRAG_TYPE, view._key);
        e.dataTransfer.setData("text/plain", view._key);
        e.dataTransfer.effectAllowed = "move";
        onDragStart?.(view._key);
    };

    const calculatePosition = (y: number, height: number) => {
        if (y < height / 4) {
            return 'top';
        }

        if (y > (height * 3) / 4) {
            return 'bottom';
        }

        if (showDropLineTop) {
            return 'top';
        }

        if (showDropLineBottom) {
            return 'bottom';
        }

        return null;
    }

    const handleDragOver = (e: React.DragEvent) => {
        // Folder drags are handled by the folder headers, not by view rows
        if (e.dataTransfer.types.includes(FOLDER_DRAG_TYPE)) {
            return;
        }
        // A root row is a shared-reorder target: refuse it (no preventDefault
        // -> browser not-allowed cursor, no drop line) while the dragged view
        // cannot be reordered by this user
        if (containerFolderId === null && !draggedViewReorderable) {
            onDragOver?.(view._key, null);
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";

        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;

        // Determine position: before 1/3 => line before, after 2/3 => line after
        const position = calculatePosition(y, height);
        onDragOver?.(view._key, position);
    };

    const handleDragLeave = () => {
        // Clear any drop line indicators when leaving
        onDragOver?.(view._key, null); // This will be handled by the parent to clear indicators
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        onDragEnd?.();

        // Folder drops land on folder headers only
        if (e.dataTransfer.getData(FOLDER_DRAG_TYPE)) return;

        const draggedViewKey = e.dataTransfer.getData(VIEW_DRAG_TYPE) || e.dataTransfer.getData("text/plain");
        if (draggedViewKey === view._key) return; // Can't drop on itself

        const draggedView = object.views.find(v => v._key === draggedViewKey);
        if (!draggedView) return;

        // Determine drop position
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;
        const position = calculatePosition(y, height);

        if (position === null) return;

        // Inside a folder the order is personal (the folder's view list):
        // delegate to the parent, no write on the shared view order and no
        // right required.
        if (containerFolderId !== null) {
            onDropInFolder?.(draggedViewKey, view._key, containerFolderId, position);
            return;
        }

        // At the root the order is the shared View.order: reordering stays
        // gated by the rights on the dragged view (the drag-over guard above
        // already refuses the target; this is a backstop — moving a view back
        // to the root without the right goes through the root drop zone)
        const canModifyDragged = draggedView.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess, draggedView._key);
        if (!canModifyDragged) {
            return;
        }

        // Calculate new order based on drop position
        const sortedViews = [...object.views].sort((a, b) => (a.order || 0) - (b.order || 0));
        const currentIndex = sortedViews.findIndex(v => v._key === view._key);

        let newOrder: number;

        if (position === 'top') {
            // Dropping above this view
            if (currentIndex === 0) {
                // Dropping at the beginning
                newOrder = (sortedViews[0].order || 0) - DEFAULT_ORDER_INCREMENT;
            } else {
                // Dropping between previous and current
                const prevOrder = sortedViews[currentIndex - 1].order || 0;
                const currentOrder = sortedViews[currentIndex].order || 0;
                newOrder = (prevOrder + currentOrder) / 2;
            }
        } else {
            // Dropping below this view
            if (currentIndex === sortedViews.length - 1) {
                // Dropping at the end
                newOrder = (sortedViews[sortedViews.length - 1].order || 0) + DEFAULT_ORDER_INCREMENT;
            } else {
                // Dropping between current and next
                const currentOrder = sortedViews[currentIndex].order || 0;
                const nextOrder = sortedViews[currentIndex + 1].order || 0;
                newOrder = (currentOrder + nextOrder) / 2;
            }
        }

        // Update the view order via API
        const updatedView = { ...draggedView, order: newOrder };

        if (!workspace?._key) {
            console.error("Workspace key is not set");
            return;
        }

        put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${object.object._key}/views`, updatedView)
            .then(response => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    }
                } else {
                    onViewOrderUpdate?.(updatedView);
                    // Dropped among the root views: the view leaves its
                    // folder if it had one (personal preference)
                    onMovedToRoot?.(draggedViewKey);
                }
            });
    };

    let style = isHover ? getWsButtonStyle(wsHoverColor, null, wsColor) : getWsButtonStyle('transparent', null, wsColor);

    const hasViewCreateRight = hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess);
    const hasFullAccess = view.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess, view._key);

    const isOwner = view.ownerKey === user?._key;
    const ownerColor = wsIsDarkColor ? wsColor : wsDarkColor;

    return (
        <>
            {showDropLineTop && <DropLineIndicator position="top" color={wsColor} />}
            <div
                ref={dragRef}
                className={`${selectedView?._key === view._key ? TW_ViewListItemSelected : TW_ViewListItem} ${isDragging ? 'opacity-50 w-3/4' : ''
                    }`}
                onClick={onSelection}
                style={style}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                draggable={true}
                onDragStart={handleDragStart}
                onDragEnd={onDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <RowVertical
                    className={`${TW_ViewListItemIcon} cursor-grab`}
                    color="red"
                />
                <TooltipProvider>
                    <Tooltip delayDuration={600} >
                        <TooltipTrigger asChild>
                            <p className={TW_ViewListItemText}>{view.name}</p>
                        </TooltipTrigger>
                        <TooltipContent>
                            {view.name}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                {(hasFullAccess || hasViewCreateRight) && <ViewMenuButton object={object} view={view} showIcon={isHover} />}
                {view.shared &&
                    <TooltipProvider>
                        <Tooltip delayDuration={100}>
                            <TooltipTrigger>
                                <People className={TW_ViewListItemSharedIcon} color={isOwner ? ownerColor : GRAY_800_COLOR} />
                            </TooltipTrigger>
                            <TooltipContent className={TW_ViewListItemTooltipContent}>
                                <div className={TW_ViewListItemTooltipText}>{t("view.shared.icon.tooltip.1")}</div>
                                <div className={TW_ViewListItemTooltipText}>{t("view.shared.icon.tooltip.2")}</div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                }
                {!view.shared &&
                    <TooltipProvider>
                        <Tooltip delayDuration={100}>
                            <TooltipTrigger>
                                <Profile className={TW_ViewListItemPrivateIcon} color={isOwner ? ownerColor : GRAY_800_COLOR} />
                            </TooltipTrigger>
                            <TooltipContent className={TW_ViewListItemTooltipContent}>
                                <div className={TW_ViewListItemTooltipText}>{t("view.private.icon.tooltip.1")}</div>
                                <div className={TW_ViewListItemTooltipText}>{t("view.private.icon.tooltip.2")}</div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                }
            </div>
            {showDropLineBottom && <DropLineIndicator position="bottom" color={wsColor} />}
        </>
    )
}

export default DraggableViewListItem;
